<template>
  <div class="cert-prod-page">
    <!-- ===================== Hero Header ===================== -->
    <div class="cp-hero">
      <div class="cp-hero__blob cp-hero__blob--one" />
      <div class="cp-hero__blob cp-hero__blob--two" />

      <div class="cp-hero__content">
        <div class="cp-hero__icon">
          <feather-icon
            icon="AwardIcon"
            size="30"
          />
        </div>
        <div>
          <h2 class="cp-hero__title">
            Certificates &amp; Their Products
          </h2>
          <p class="cp-hero__subtitle">
            Browse every certificate and the product categories it covers.
          </p>
        </div>
      </div>

      <div class="cp-hero__actions">
        <button
          type="button"
          class="cp-back-btn"
          @click="goBack"
        >
          <span class="cp-back-btn__icon">
            <feather-icon
              icon="ArrowLeftIcon"
              size="16"
            />
          </span>
          <span class="text-nowrap">Back</span>
        </button>

        <button
          type="button"
          class="cp-add-btn"
          @click="goToAdd"
        >
          <span class="cp-add-btn__icon">
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
          </span>
          <span class="text-nowrap">{{ addType }}</span>
        </button>
      </div>
    </div>

    <!-- ===================== Stats Header ===================== -->
    <b-row class="match-height mb-1">
      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--primary"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalCertificates }}
              </h2>
              <span class="text-white-75">Certificates</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="AwardIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="AwardIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--info"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.pageCertificates }}
              </h2>
              <span class="text-white-75">On This Page</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="GridIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="GridIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--warning"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalProductLinks }}
              </h2>
              <span class="text-white-75">Linked Products</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="PackageIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="PackageIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>

      <b-col
        cols="12"
        sm="6"
        lg="3"
      >
        <b-card
          no-body
          class="stat-card stat-card--success"
        >
          <b-card-body class="d-flex justify-content-between align-items-center">
            <div>
              <h2 class="font-weight-bolder mb-0 text-white">
                {{ stats.totalGroups }}
              </h2>
              <span class="text-white-75">Certificate Groups</span>
            </div>
            <b-avatar
              size="52"
              class="stat-avatar"
            >
              <feather-icon
                icon="LayersIcon"
                size="26"
              />
            </b-avatar>
          </b-card-body>
          <feather-icon
            icon="LayersIcon"
            size="90"
            class="stat-card__ghost-icon"
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- ===================== Toolbar ===================== -->
    <b-card
      no-body
      class="cp-toolbar-card mb-1"
    >
      <div class="cp-toolbar">
        <div class="cp-toolbar__title">
          <b-avatar
            rounded
            variant="light-primary"
            size="40"
            class="mr-1"
          >
            <feather-icon
              icon="ListIcon"
              size="20"
            />
          </b-avatar>
          <div>
            <h4 class="mb-0 font-weight-bolder">
              Certificates
            </h4>
            <small class="text-muted">Every certificate and the products it applies to</small>
          </div>
        </div>

        <div class="cp-toolbar__actions">
          <b-input-group class="cp-search">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model="searchQuery"
              placeholder="Search certificates... (press Enter)"
              autocomplete="off"
              @keyup.enter="searchNow"
            />
            <b-input-group-append
              v-if="searchQuery"
              is-text
              class="cp-search__clear"
              @click="clearSearch"
            >
              <feather-icon icon="XIcon" />
            </b-input-group-append>
          </b-input-group>
        </div>
      </div>
    </b-card>

    <!-- ===================== Loading ===================== -->
    <b-row v-if="dataLoad">
      <b-col
        v-for="n in 6"
        :key="n"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="cert-skel-card">
          <div class="cert-skel-accent" />
          <div class="cert-skel-head">
            <div class="cert-skel-avatar" />
            <div class="cert-skel-lines">
              <div class="cert-skel-line cert-skel-line--short" />
              <div class="cert-skel-line cert-skel-line--long" />
            </div>
          </div>
          <div class="cert-skel-body">
            <div class="cert-skel-chip" />
            <div class="cert-skel-chip" />
            <div class="cert-skel-chip" />
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Empty state ===================== -->
    <b-card
      v-else-if="!rows.length"
      class="text-center py-5"
    >
      <feather-icon
        icon="InboxIcon"
        size="48"
        class="text-muted mb-1"
      />
      <h4>No certificates found</h4>
      <p class="text-muted mb-1">
        {{ searchQuery ? 'Try adjusting your search.' : 'Start by adding your first certificate.' }}
      </p>
      <button
        v-if="!searchQuery"
        type="button"
        class="cp-add-btn mx-auto"
        @click="goToAdd"
      >
        <span class="cp-add-btn__icon">
          <feather-icon
            icon="PlusIcon"
            size="16"
          />
        </span>
        <span class="text-nowrap">{{ addType }}</span>
      </button>
    </b-card>

    <!-- ===================== Certificate cards grid ===================== -->
    <b-row
      v-else
      class="certs-grid"
    >
      <b-col
        v-for="row in rows"
        :key="row.id"
        cols="12"
        md="6"
        xl="4"
      >
        <div class="cert-card">
          <div
            class="cert-accent"
            :style="{ background: getCertGradient(row) }"
          />

          <!-- Header -->
          <div class="cert-card-head">
            <div
              class="cert-avatar"
              :style="{ background: getCertGradient(row) }"
            >
              <feather-icon
                :icon="getCertIcon(row)"
                size="22"
              />
            </div>
            <div class="cert-head-text">
              <h4
                class="cert-name"
                :title="row.name && (row.name.en || row.name.ar)"
              >
                {{ row.name && (row.name.en || row.name.ar) || 'Untitled certificate' }}
              </h4>
              <span
                v-if="row.name && row.name.ar && row.name.en && row.name.ar !== row.name.en"
                class="cert-name-ar"
                dir="rtl"
              >{{ row.name.ar }}</span>
            </div>
          </div>

          <div class="cert-card-body">
            <!-- Certificate group -->
            <div
              v-if="row.certificate_group && row.certificate_group.name"
              class="cert-group-chip"
              :style="{ color: getGroupMeta(row.certificate_group).color, background: getGroupMeta(row.certificate_group).soft }"
            >
              <feather-icon
                :icon="getGroupMeta(row.certificate_group).icon"
                size="12"
                class="mr-25"
              />
              {{ row.certificate_group.name.en || row.certificate_group.name.ar }}
            </div>

            <!-- Product categories -->
            <div class="cert-section">
              <div class="section-label">
                <feather-icon
                  icon="PackageIcon"
                  size="14"
                  class="mr-25"
                />
                <span>Product Categories</span>
                <b-badge
                  pill
                  variant="light-primary"
                  class="ml-50"
                >
                  {{ getUniqueCategories(row).length }}
                </b-badge>
              </div>

              <div
                v-if="getUniqueCategories(row).length"
                class="category-chips"
              >
                <span
                  v-for="cat in getUniqueCategories(row)"
                  :key="cat.id"
                  class="category-chip"
                >
                  {{ cat.name }}
                </span>
              </div>
              <span
                v-else
                class="empty-hint"
              >No product categories linked</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="cert-card-footer">
            <a
              :href="getEditLink(row)"
              target="_blank"
              rel="noopener"
              class="footer-btn footer-btn--edit"
            >
              <feather-icon
                icon="EditIcon"
                size="14"
                class="mr-25"
              />
              Edit
            </a>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- ===================== Pagination ===================== -->
    <div
      v-if="!dataLoad && rows.length"
      class="mt-1"
    >
      <Pagination
        :page="page"
        :per-page="perPage"
        :total-rows="totalRows"
        @get-all-data="getAllData"
      />
    </div>
  </div>
</template>

<script>
/* eslint-disable camelcase */
import axios from 'axios'
import {
  BRow, BCol, BCard, BCardBody, BAvatar, BBadge,
  BInputGroup, BInputGroupPrepend, BInputGroupAppend, BFormInput,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import Pagination from '@/components/general-table/specific-utils/Pagination.vue'

const CERT_ICON_RULES = [
  { keys: ['electrical', 'electronic'], icon: 'ZapIcon' },
  { keys: ['chemical', 'petroleum', 'textile'], icon: 'ThermometerIcon' },
  { keys: ['mechanical', 'metallurgical'], icon: 'ToolIcon' },
  { keys: ['construction', 'building', 'general materials'], icon: 'HomeIcon' },
  { keys: ['plastic'], icon: 'BoxIcon' },
  { keys: ['vehicle', 'cargo', 'trailer'], icon: 'TruckIcon' },
  { keys: ['food', 'agriculture'], icon: 'CoffeeIcon' },
  { keys: ['water'], icon: 'DropletIcon' },
]

const CERT_ICON_FALLBACK = ['AwardIcon', 'ShieldIcon', 'BookmarkIcon', 'TagIcon']

const CERT_GRADIENTS = [
  'linear-gradient(135deg, #7367f0, #9e95f5)',
  'linear-gradient(135deg, #28c76f, #48da89)',
  'linear-gradient(135deg, #00cfe8, #1ce7ff)',
  'linear-gradient(135deg, #ff9f43, #ffbc7d)',
  'linear-gradient(135deg, #ea5455, #f97066)',
  'linear-gradient(135deg, #818cf8, #4f46e5)',
  'linear-gradient(135deg, #22d3ee, #0891b2)',
  'linear-gradient(135deg, #f6d365, #fda085)',
]

const GROUP_PALETTE = [
  { color: '#7367f0', soft: 'rgba(115, 103, 240, 0.12)', icon: 'TagIcon' },
  { color: '#28c76f', soft: 'rgba(40, 199, 111, 0.12)', icon: 'CheckCircleIcon' },
  { color: '#00cfe8', soft: 'rgba(0, 207, 232, 0.12)', icon: 'ShieldIcon' },
  { color: '#ff9f43', soft: 'rgba(255, 159, 67, 0.12)', icon: 'AwardIcon' },
  { color: '#ea5455', soft: 'rgba(234, 84, 85, 0.12)', icon: 'AlertCircleIcon' },
  { color: '#4f46e5', soft: 'rgba(79, 70, 229, 0.12)', icon: 'BookmarkIcon' },
]
const DEFAULT_GROUP_META = { color: '#82868b', soft: 'rgba(130, 134, 139, 0.12)', icon: 'TagIcon' }

export default {
  components: {
    Pagination,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BAvatar,
    BBadge,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BFormInput,
  },
  data() {
    return {
      apiUrl: 'certificates-with-products',
      addType: 'Add Product To Certificate',
      addComponentName: 'add-product-to-certificate',
      editComponent: 'edit-product-to-certificate',
      rows: [],
      totalRows: 0,
      page: 1,
      perPage: 20,
      dataLoad: false,
      searchQuery: '',
      stats: {
        totalCertificates: 0,
        pageCertificates: 0,
        totalProductLinks: 0,
        totalGroups: 0,
      },
    }
  },
  mounted() {
    this.getAllData(this.page)
  },
  methods: {
    goBack() {
      this.$router.push({ name: 'product-and-certificate' })
    },

    goToAdd() {
      this.$router.push({ name: this.addComponentName })
    },

    async getAllData(page = 1) {
      this.page = page
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
          ...(this.searchQuery ? { search: this.searchQuery } : {}),
        }

        const response = await axios.get(this.apiUrl, { params })

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const firstKey = Object.keys(responseData?.data)[0]
          this.rows = responseData?.data[firstKey] || []
          this.totalRows = responseData.data.count || this.rows.length
          this.computeStats()
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Failed to load certificates',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.dataLoad = false
      }
    },

    searchNow() {
      this.getAllData(1)
    },

    clearSearch() {
      this.searchQuery = ''
      this.getAllData(1)
    },

    computeStats() {
      const groups = new Set()
      let productLinks = 0

      this.rows.forEach(item => {
        productLinks += this.getUniqueCategories(item).length
        if (item.certificate_group) groups.add(item.certificate_group.id)
      })

      this.stats = {
        totalCertificates: this.totalRows,
        pageCertificates: this.rows.length,
        totalProductLinks: productLinks,
        totalGroups: groups.size,
      }
    },

    getUniqueCategories(row) {
      const categories = row.product_categories || []
      const seen = new Set()
      const unique = []

      categories.forEach(cat => {
        if (!seen.has(cat.id)) {
          seen.add(cat.id)
          unique.push(cat)
        }
      })

      return unique
    },

    getEditLink(row) {
      const formattedName = (row.name && (row.name.en || row.name.ar)) || ''

      return this.$router.resolve({
        name: this.editComponent,
        params: { id: row.id, name: formattedName },
      }).href
    },

    getCertIcon(row) {
      const name = ((row.name && (row.name.en || row.name.ar)) || '').toLowerCase()
      const rule = CERT_ICON_RULES.find(({ keys }) => keys.some(key => name.includes(key)))

      if (rule) return rule.icon

      return CERT_ICON_FALLBACK[row.id % CERT_ICON_FALLBACK.length]
    },

    getCertGradient(row) {
      const key = `${row.id}`
      let hash = 0
      for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) % 1000000007
      }
      return CERT_GRADIENTS[Math.abs(hash) % CERT_GRADIENTS.length]
    },

    getGroupMeta(group) {
      if (!group) return DEFAULT_GROUP_META
      return GROUP_PALETTE[group.id % GROUP_PALETTE.length]
    },
  },
}
</script>

<style lang="scss" scoped src="./CertificateProduct.scss"></style>
